import { customElement } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { BaseShape } from './base-shape.js'
import { isGeoJSONSource } from '../map-engine.js'

interface CornerMarker extends maplibregl.Marker {
  _cornerIndex?: number
}
import { db } from '../../../database/schema.js'
import { getFillPaintProperties } from './fill-patterns.js'
import {
  calculateRectangleCenter,
  offsetRectangleCorners,
  buildClosedRectanglePolygon,
} from '../../../lib/shape-utils.js'
import { addShapeLayer } from '../layer-order-manager.js'

@customElement('shape-rectangle')
export class ShapeRectangle extends BaseShape {
  private cornerHandles: maplibregl.Marker[] = []
  private draggedCornerIndex?: number | undefined

  protected createLayers(): void {
    const geojson = this.getGeoJSON()
    this.map.addSource(this.sourceId, { type: 'geojson', data: geojson })

    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`

    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')

    addShapeLayer(this.map, {
      id: fillLayerId,
      type: 'fill',
      source: this.sourceId,
      paint: {
        'fill-color': fillProps.fillColor,
        'fill-opacity': fillProps.fillOpacity,
      },
    })

    const dashArray = this.feature.style?.strokeDashArray ?? undefined
    addShapeLayer(this.map, {
      id: strokeLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': this.feature.style?.strokeColor ?? '#3f5a7a',
        'line-width': this.feature.style?.strokeWidth ?? 2,
        'line-opacity': this.feature.style?.strokeOpacity ?? 0.8,
        ...(dashArray ? { 'line-dasharray': dashArray } : {}),
      },
    })

    this.layerIds = [fillLayerId, strokeLayerId]
  }

  getGeoJSON(): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: (this.feature.geometry as GeoJSON.Polygon).coordinates,
      },
    }
  }

  override refreshStyle(): void {
    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`
    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')

    if (this.map.getLayer(fillLayerId)) {
      this.map.setPaintProperty(fillLayerId, 'fill-color', fillProps.fillColor)
      this.map.setPaintProperty(fillLayerId, 'fill-opacity', fillProps.fillOpacity)
    }
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-color',
        this.feature.style?.strokeColor ?? '#3f5a7a'
      )
      this.map.setPaintProperty(strokeLayerId, 'line-width', this.feature.style?.strokeWidth ?? 2)
      this.map.setPaintProperty(
        strokeLayerId,
        'line-opacity',
        this.feature.style?.strokeOpacity ?? 0.8
      )
      const dashArray = this.feature.style?.strokeDashArray
      this.map.setPaintProperty(
        strokeLayerId,
        'line-dasharray',
        dashArray?.length ? dashArray : (null as number[] | null)
      )
    }
    this.applyFillPatternToLayer()
  }

  updateSource(): void {
    const source = this.map.getSource(this.sourceId)
    if (isGeoJSONSource(source)) {
      source.setData(this.getGeoJSON())
    }
  }

  protected createHandles(): void {
    this.cornerHandles = []

    const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
    const corners = coords.slice(0, -1)

    corners.forEach((coord: number[], index: number) => {
      const cornerHandle = this.createHandle([coord[0], coord[1]], 'corner')
      ;(cornerHandle as CornerMarker)._cornerIndex = index
      this.cornerHandles.push(cornerHandle)
      this.handles.push(cornerHandle)
    })

    this.setupRectangleDragging()
  }

  protected override setupHandleDragging(marker: maplibregl.Marker, handleType: string): void {
    const element = marker.getElement()
    const initDrag = (): void => {
      if (handleType === 'corner') {
        this.draggedCornerIndex = (marker as CornerMarker)._cornerIndex
      }
      this.startHandleDrag(marker, handleType)
    }
    element.addEventListener('mousedown', (e): void => {
      e.stopPropagation()
      e.preventDefault()
      initDrag()
    })
    element.addEventListener(
      'touchstart',
      (e): void => {
        e.preventDefault()
        e.stopPropagation()
        initDrag()
      },
      { passive: false }
    )
  }

  protected updateShapePreview(handleType: string, newPosition: maplibregl.LngLat): void {
    if (handleType === 'drag') {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const corners = coords.slice(0, -1)
      const [currentCenterLng, currentCenterLat] = calculateRectangleCenter(corners)
      const offsetLng = newPosition.lng - currentCenterLng
      const offsetLat = newPosition.lat - currentCenterLat
      const newCorners = offsetRectangleCorners(corners, offsetLng, offsetLat)
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: buildClosedRectanglePolygon(newCorners) },
        })
      }
    } else if (handleType === 'corner' && this.draggedCornerIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const corners = coords.slice(0, -1)
      const newCorners = [...corners]

      newCorners[this.draggedCornerIndex] = [newPosition.lng, newPosition.lat]

      if (corners.length === 4) {
        const oppositeIndex = (this.draggedCornerIndex + 2) % 4
        const oppositeCorner = corners[oppositeIndex]
        const prevIndex = (this.draggedCornerIndex + 3) % 4
        const nextIndex = (this.draggedCornerIndex + 1) % 4
        newCorners[prevIndex] = [oppositeCorner[0], newPosition.lat]
        newCorners[nextIndex] = [newPosition.lng, oppositeCorner[1]]
      }

      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: buildClosedRectanglePolygon(newCorners) },
        })
      }
    }
  }

  protected async applyGeometryChange(
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    if (handleType === 'drag') {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const corners = coords.slice(0, -1)
      const [currentCenterLng, currentCenterLat] = calculateRectangleCenter(corners)
      const offsetLng = newPosition.lng - currentCenterLng
      const offsetLat = newPosition.lat - currentCenterLat
      const newCorners = offsetRectangleCorners(corners, offsetLng, offsetLat)
      const newGeometry: GeoJSON.Polygon = {
        type: 'Polygon',
        coordinates: buildClosedRectanglePolygon(newCorners),
      }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
    } else if (handleType === 'corner' && this.draggedCornerIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const corners = coords.slice(0, -1)
      const newCorners = [...corners]

      newCorners[this.draggedCornerIndex] = [newPosition.lng, newPosition.lat]

      if (corners.length === 4) {
        const oppositeIndex = (this.draggedCornerIndex + 2) % 4
        const oppositeCorner = corners[oppositeIndex]
        const prevIndex = (this.draggedCornerIndex + 3) % 4
        const nextIndex = (this.draggedCornerIndex + 1) % 4
        newCorners[prevIndex] = [oppositeCorner[0], newPosition.lat]
        newCorners[nextIndex] = [newPosition.lng, oppositeCorner[1]]
      }

      const newGeometry: GeoJSON.Polygon = {
        type: 'Polygon',
        coordinates: buildClosedRectanglePolygon(newCorners),
      }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
      this.draggedCornerIndex = undefined
    }
  }

  protected updateAllHandlePositions(centerPosition: maplibregl.LngLat): void {
    const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
    const corners = coords.slice(0, -1)
    const [currentCenterLng, currentCenterLat] = calculateRectangleCenter(corners)
    const offsetLng = centerPosition.lng - currentCenterLng
    const offsetLat = centerPosition.lat - currentCenterLat
    const moved = offsetRectangleCorners(corners, offsetLng, offsetLat)

    this.cornerHandles.forEach((handle, index) => {
      if (index < moved.length) {
        handle.setLngLat([moved[index][0], moved[index][1]])
      }
    })
  }

  private setupRectangleDragging(): void {
    const fillLayerId = `${this.sourceId}-fill`

    this.map.on('mousedown', fillLayerId, e => {
      if (!this.isEditing) {
        return
      }
      e.preventDefault()
      this.startShapeDrag(e.lngLat)
    })
  }

  private _dragMouseMove?: ((e: MouseEvent) => void) | undefined
  private _dragMouseUp?: ((e: MouseEvent) => void) | undefined
  private _dragTouchMove?: ((e: TouchEvent) => void) | undefined
  private _dragTouchEnd?: ((e: TouchEvent) => void) | undefined
  private startShapeDrag(_startPosition: maplibregl.LngLat): void {
    if (this.isDragging) {
      return
    }
    this.isDragging = true
    this.handles.forEach(h => (h.getElement().style.display = 'none'))

    this._dragMouseMove = (e: MouseEvent) => {
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      this.updateShapePreview('drag', lngLat)
    }
    this._dragMouseUp = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      void this.endShapeDrag(lngLat)
    }
    this._dragTouchMove = (e: TouchEvent) => {
      if (!this.isDragging || e.touches.length !== 1) {
        return
      }
      const lngLat = this.touchToLngLat(e.touches[0])
      e.preventDefault()
      this.updateShapePreview('drag', lngLat)
    }
    this._dragTouchEnd = (e: TouchEvent) => {
      if (!this.isDragging) {
        return
      }
      const lngLat = e.changedTouches.length > 0 ? this.touchToLngLat(e.changedTouches[0]) : null
      if (!lngLat) {
        return
      }
      e.preventDefault()
      void this.endShapeDrag(lngLat)
    }

    document.addEventListener('mousemove', this._dragMouseMove)
    document.addEventListener('mouseup', this._dragMouseUp)
    this.map.getContainer().addEventListener('touchmove', this._dragTouchMove, { passive: false })
    this.map.getContainer().addEventListener('touchend', this._dragTouchEnd, { passive: false })
  }

  private async endShapeDrag(newPosition: maplibregl.LngLat): Promise<void> {
    if (!this.isDragging) {
      return
    }
    this.isDragging = false
    BaseShape.ignoreNextMapClick++
    setTimeout(() => {
      if (BaseShape.ignoreNextMapClick > 0) {
        BaseShape.ignoreNextMapClick = 0
      }
    }, 200)

    if (this._dragMouseMove) {
      document.removeEventListener('mousemove', this._dragMouseMove)
    }
    if (this._dragMouseUp) {
      document.removeEventListener('mouseup', this._dragMouseUp)
    }
    if (this._dragTouchMove) {
      this.map.getContainer().removeEventListener('touchmove', this._dragTouchMove)
    }
    if (this._dragTouchEnd) {
      this.map.getContainer().removeEventListener('touchend', this._dragTouchEnd)
    }
    this._dragMouseMove = undefined
    this._dragMouseUp = undefined
    this._dragTouchMove = undefined
    this._dragTouchEnd = undefined

    await this.applyGeometryChange('drag', newPosition)
    if (this.isEditing) {
      this.removeHandles()
      this.createHandles()
    }
  }

  protected override applySelectionStyle(selected: boolean): void {
    const strokeLayerId = `${this.sourceId}-stroke`
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-width',
        selected
          ? (this.feature.style?.strokeWidth ?? 2) + 2
          : (this.feature.style?.strokeWidth ?? 2)
      )
    }
  }
}
